import { useMemo, useCallback, useState, useEffect } from 'react';

const itemsToSelection = (items, getId) =>
  items.reduce(([acc, item]) => ({ ...acc, [getId(item)]: item }), {});

export const useSelection = ({
  data = [],
  initialSelection = [],
  multiple = true,
  getId = (x) => x.id,
}) => {
  const [selection, setSelection] = useState(() =>
    itemsToSelection(initialSelection, getId),
  );

  // --- CLEANUP WHEN DATA CHANGES ---
  useEffect(() => {
    const validIds = new Set(data.map(getId));
    setSelection((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id]) => validIds.has(id)),
      );
      // prevent unnecessary state updates
      return Object.keys(next).length === Object.keys(prev).length &&
        Object.keys(next).every((k) => prev[k] === next[k])
        ? prev
        : next;
    });
  }, [data, getId]);

  const select = useCallback(
    (item) => {
      if (multiple) {
        setSelection((p) => ({ ...p, [getId(item)]: item }));
      } else {
        setSelection({ [getId(item)]: item });
      }
    },
    [multiple, getId],
  );

  const unselect = useCallback(
    (item) => {
      setSelection((p) => {
        const id = getId(item);
        if (!p[id]) return p;
        const next = { ...p };
        delete next[id];
        return next;
      });
    },
    [getId],
  );

  const selectAll = useCallback(() => {
    if (!multiple) throw Error('cant selectAll in single mode');
    setSelection(itemsToSelection(data, getId));
  }, [multiple, data, getId]);

  const unselectAll = useCallback(() => setSelection({}), []);

  const isSelected = useCallback(
    (item) => Boolean(selection[getId(item)]),
    [selection, getId],
  );

  const toggle = useCallback(
    (item) => {
      isSelected(item) ? unselect(item) : select(item);
    },
    [isSelected, select, unselect],
  );

  const selected = useMemo(() => Object.values(selection), [selection]);
  const selectedIds = useMemo(() => Object.keys(selection), [selection]);

  return {
    selected: multiple ? selected : selected.at(0),
    selectedIds,
    selectAll,
    unselectAll,
    select,
    unselect,
    toggle,
    isSelected,
  };
};
